// @ts-strict-ignore
// eslint-disable-next-line no-restricted-imports
import produce, {Draft} from 'immer';
import * as React from 'react';

import {DisplayBookmark} from 'common/report/dashboards/types';
import {BehaviorsResource} from 'common/report/model-resources/behaviors';
import {MetricsResource} from 'common/report/model-resources/metrics';
import {QueryReason} from 'common/report/util/query-tracking/types';
import {InsightsBookmarkParams} from 'common/types/reports/bookmark';

import {syncMetricsAcrossBookmark} from './helpers';
import {hasValidBookmarkParams} from './validation';

type ParamsMutator = (s: Draft<InsightsBookmarkParams>) => void;

type Bookmark = DisplayBookmark<InsightsBookmarkParams>;
type ReportState = Bookmark & {
  /** provides an immutable setter on bookmark params  */
  updateParams: (fn: ParamsMutator) => void;
  isValid: () => boolean;
};

/** Options passed on every UI interaction that leads to a bookmark param update */
type ApplyParamsDetail = {queryReason?: QueryReason; requery?: boolean; hasChangedChartType?: boolean};

/**
 * Provides a Panel context that hosts a @type{Bookmark} instance and utilities to apply immutable modifiers
 *
 * This context also provides an instance of @type{ReportState} which is propagated as a React context value
 * inside @see{useReportBookmark}. This is done to avoid propagating Panel context internals to React components
 * in React hooks.
 */
export class ReportBookmarkManager {
  private declare _onApplyParams: (params: InsightsBookmarkParams, options?: ApplyParamsDetail) => void;
  private _bookmark: Bookmark | null = null;
  private _committedBookmark: Bookmark | null = null;

  private metrics: MetricsResource = null;
  private behaviors: BehaviorsResource = null;

  get exposedValue(): ReportState {
    return {
      ...this._bookmark,

      /** stages any bookmark changes, if they are valid they are committed and re-queried */
      updateParams: (fn, options: ApplyParamsDetail = {}) => {
        const updatedBookmark = produce(this._bookmark, (draft) => {
          fn(draft.params);

          /**
           * makes sure any edits to saved entities are reflected in the bookmark
           * this isn't necessary when loading a bookmark since the backend
           * will return a fully hydrated bookmark
           */
          syncMetricsAcrossBookmark({
            params: draft.params,
            metrics: this.metrics,
            behaviors: this.behaviors,
          });
        });

        this._bookmark = updatedBookmark;

        if (hasValidBookmarkParams(updatedBookmark.params)) {
          this._committedBookmark = updatedBookmark;
          this._onApplyParams(this._committedBookmark.params, options);
        }
      },

      isValid(): boolean {
        return hasValidBookmarkParams(this.params);
      },
    };
  }

  public syncBookmark(bookmark: Bookmark) {
    this._committedBookmark = bookmark;
    this._bookmark = bookmark;
  }

  public setup(
    onApplyParams: typeof this._onApplyParams,
    {metrics, behaviors}: {metrics: MetricsResource; behaviors: BehaviorsResource},
  ) {
    this._onApplyParams = onApplyParams;
    this.metrics = metrics;
    this.behaviors = behaviors;
  }

  public teardown() {
    this._onApplyParams = null;
    this._bookmark = null;
    this._committedBookmark = null;
  }
}

const ReportBookmarkManagerContext = React.createContext<ReportState>(
  // @ts-ignore
  {
    params: null,
  },
);

export const ReportBookmarkManagerConsumer = ReportBookmarkManagerContext.Consumer;

/**
 * Context provider for the ReportBookmark Panel context.
 *
 * This prevents leaking Panel context details for React components
 */
export function ReportBookmarkManagerProvider(props: {
  children: React.ReactNode;
  value: ReportBookmarkManager | ReportState;
}) {
  const contextValue = props.value instanceof ReportBookmarkManager ? props.value.exposedValue : props.value;
  return (
    <ReportBookmarkManagerContext.Provider value={new Proxy(contextValue, {})}>
      {props.children}
    </ReportBookmarkManagerContext.Provider>
  );
}

export const useReportBookmark = (): ReportState => {
  const contextValue = React.useContext(ReportBookmarkManagerContext);
  if (contextValue instanceof ReportBookmarkManager) {
    throw new Error(`Attempting to use a Panel context inside a React component`);
  }

  return contextValue;
};
